import { Search, UserPlus } from "lucide-react";
import { useCallback, useId, useState } from "react";

import {
  isDirectoryUnavailable,
  useDirectoryResolveQuery,
  useRoleAssignmentsQuery,
  useRoleCatalogQuery,
} from "@/api/access";
import { ApiError } from "@/api/client";
import { queryKeys } from "@/api/hooks";
import { QueryErrorState } from "@/api/QueryErrorState";
import { QueryPendingState } from "@/api/QueryPendingState";
import { isTerminalQueryError } from "@/api/queryClient";
import { useCapability, useSession } from "@/auth/SessionContext";
import {
  describeSubject,
  groupAssignmentsBySubject,
  rowMatches,
  sortRowsByIdentity,
  type SubjectAccessRow,
} from "@/lib/editorAccess";
import { PageHeader } from "@/shell/PageHeader";
import { AccessHistorySection, SubjectHistoryDialog } from "@/ui/access/AccessHistory";
import { AccessTable } from "@/ui/access/AccessTable";
import { GrantAccessDialog } from "@/ui/access/GrantAccessDialog";
import { RemoveAccessDialog } from "@/ui/access/RemoveAccessDialog";
import { Button } from "@/ui/Button";
import { Card } from "@/ui/Card";
import { Input } from "@/ui/Input";
import { Notice } from "@/ui/Notice";
import { EmptyState, StaleDataNotice } from "@/ui/states";

export const USER_ADMINISTRATION_DESCRIPTION =
  "Manage who can use the Clinical Editor and what they can do. Accounts come from your organization's Entra directory; this page grants Editor access and does not create user accounts.";

/**
 * User administration: which existing Entra identities may use the Clinical
 * Editor, and with which roles.
 *
 * ## What decides access here, and what does not
 *
 * The Editor Service does. This route renders `GET /v1/role-assignments` —
 * live and revoked, grouped one row per Entra subject — and writes through
 * `PUT /v1/subject-roles`, which replaces a person's live role set atomically
 * under the service's own stale-write guard. A non-administrator who types the
 * address reaches the same request and receives the service's 403, rendered
 * as the denied state: the navigation entry is hidden on the session's
 * capability, but that hiding is presentation, and the refusal is the
 * server's. Nothing on this page re-derives what a role means; the role
 * catalog and the capability list arrive from the service.
 *
 * ## Identity, enrichment, and the two things that are not the same
 *
 * The Entra object id is the authorization key and the only value a grant
 * stores. Display names and emails are enrichment from
 * `POST /v1/directory/users/resolve`, shown beside the id when a directory
 * answers and replaced by "Unknown Entra user" when it does not — never by a
 * guess. The signed-in administrator's own row takes its name from the
 * session, which vouches for exactly that one identity. GitHub identity is
 * deliberately absent: no Admin-facing API maps another subject to a GitHub
 * login, and a column inferred from a similar name would be a false mapping.
 */
export function UserAdministrationRoute() {
  const { session } = useSession();
  const self = {
    subject: session.user.subject,
    displayName: session.user.displayName,
  };
  const manage = useCapability("authz.manage");
  const searchInputId = useId();

  const [search, setSearch] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [granting, setGranting] = useState(false);
  const [editing, setEditing] = useState<SubjectAccessRow | null>(null);
  const [removing, setRemoving] = useState<SubjectAccessRow | null>(null);
  const [historyFor, setHistoryFor] = useState<SubjectAccessRow | null>(null);

  const assignments = useRoleAssignmentsQuery();
  const accessDenied = assignments.isError && isTerminalQueryError(assignments.error);
  const grouped = assignments.data && !accessDenied
    ? groupAssignmentsBySubject(assignments.data.items)
    : null;
  const catalog = useRoleCatalogQuery(assignments.isSuccess);
  const resolution = useDirectoryResolveQuery(grouped?.subjects ?? []);
  const directoryUnavailable =
    resolution.isError && isDirectoryUnavailable(resolution.error);
  const resolutionFailedOtherwise =
    resolution.isError && !directoryUnavailable;

  const identityOf = useCallback(
    (subject: string) => describeSubject(subject, resolution.data, self),
    // `self` is rebuilt per render from the session; its two strings are the
    // dependency that matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resolution.data, self.subject, self.displayName],
  );

  const liveRows = grouped ? sortRowsByIdentity(grouped.live, identityOf) : [];
  const visibleRows = liveRows.filter((row) =>
    rowMatches(row, identityOf(row.subject), search),
  );

  // Edits and removals keep the snapshot the administrator opened. Updating
  // its expected ids during a background refresh would acknowledge unseen
  // changes and defeat the service's compare-and-set protection.
  const editingRow = accessDenied ? null : editing;
  const removingRow = accessDenied ? null : removing;
  const historyRow = accessDenied ? null : historyFor;

  return (
    <>
      <PageHeader
        title="User administration"
        description={USER_ADMINISTRATION_DESCRIPTION}
        actions={
          <Button
            disabled={!manage.allowed}
            title={manage.reason ?? undefined}
            onClick={() => {
              if (manage.allowed) setGranting(true);
            }}
          >
            <UserPlus aria-hidden="true" className="h-4 w-4" />
            Grant access
          </Button>
        }
      />

      {granting && !accessDenied ? (
        <GrantAccessDialog
          mode="grant"
          rows={liveRows}
          catalog={catalog.data}
          catalogError={catalog.isError ? catalog.error : null}
          onCatalogRetry={() => void catalog.refetch()}
          self={self}
          onClose={() => setGranting(false)}
        />
      ) : null}
      {editingRow ? (
        <GrantAccessDialog
          mode="edit"
          row={editingRow}
          identity={identityOf(editingRow.subject)}
          rows={liveRows}
          catalog={catalog.data}
          catalogError={catalog.isError ? catalog.error : null}
          onCatalogRetry={() => void catalog.refetch()}
          self={self}
          onClose={() => setEditing(null)}
        />
      ) : null}
      {removingRow ? (
        <RemoveAccessDialog
          row={removingRow}
          identity={identityOf(removingRow.subject)}
          self={self}
          onClose={() => setRemoving(null)}
        />
      ) : null}
      {historyRow ? (
        <SubjectHistoryDialog
          identity={identityOf(historyRow.subject)}
          live={historyRow.assignments}
          revoked={historyRow.history}
          identityOf={identityOf}
          onClose={() => setHistoryFor(null)}
        />
      ) : null}

      <div className="space-y-4">
        <label htmlFor={searchInputId} className="sr-only">
          Search assigned users
        </label>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            id={searchInputId}
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search assigned users"
            leadingIcon={<Search className="h-4 w-4" />}
            className="min-w-[12rem] flex-1"
          />
          <Button
            variant="outline"
            aria-pressed={showHistory}
            onClick={() => setShowHistory((value) => !value)}
          >
            Show access history
          </Button>
        </div>

        {directoryUnavailable ? (
          <Notice tone="warning">
            Directory lookup is unavailable. People are shown by Entra Object ID;
            their Editor access is unchanged. Try again, or ask your platform team
            to check the directory connection if the problem continues.
          </Notice>
        ) : null}
        {resolutionFailedOtherwise ? (
          <Notice tone="warning">
            Names could not be resolved from the directory
            {resolution.error instanceof ApiError
              ? ` (${resolution.error.code})`
              : ""}
            . People are shown by Entra Object ID until it answers again.
          </Notice>
        ) : null}
        {resolution.isError ? (
          <Button
            variant="outline"
            size="sm"
            disabled={resolution.isFetching}
            onClick={() => void resolution.refetch()}
          >
            Try name lookup again
          </Button>
        ) : null}

        {assignments.data ? (
          <StaleDataNotice
            updatedAt={assignments.dataUpdatedAt}
            onRetry={() => void assignments.refetch()}
            visible={
              assignments.isError && !isTerminalQueryError(assignments.error)
            }
          />
        ) : null}

        {assignments.isPending ? (
          <QueryPendingState
            isPaused={assignments.isPaused}
            failureReason={assignments.failureReason}
            label="Loading Editor access"
            errorTitle="Editor access could not be loaded"
            rows={4}
            queryKey={queryKeys.roleAssignments}
          />
        ) : assignments.isError &&
          (!assignments.data || isTerminalQueryError(assignments.error)) ? (
          <QueryErrorState
            error={assignments.error}
            subject="Editor access assignments"
            title="Editor access could not be loaded"
            description="The Editor Service did not return the role assignments for this environment."
            deniedDescription="You are not authorized to administer Editor access in this environment. Only an Administrator can open User administration."
            onRetry={() => void assignments.refetch()}
          />
        ) : liveRows.length === 0 && !search ? (
          <EmptyState
            title="No one has Editor access yet"
            description="People granted a role will appear here, one row per Entra identity. Use Grant access to select an existing person from the directory."
          />
        ) : (
          <Card className="overflow-hidden">
            {visibleRows.length === 0 ? (
              <div
                role="status"
                className="px-4 py-8 text-center text-sm text-muted-foreground"
              >
                No assigned users match your search.
              </div>
            ) : (
              <AccessTable
                rows={visibleRows}
                identityOf={identityOf}
                catalog={catalog.data}
                self={self}
                onEdit={setEditing}
                onRemove={setRemoving}
                onHistory={setHistoryFor}
              />
            )}
          </Card>
        )}

        {showHistory && grouped ? (
          <AccessHistorySection
            revoked={grouped.revoked}
            identityOf={identityOf}
          />
        ) : null}

        <p className="text-xs text-muted-foreground">
          Changes take effect on the person's next authenticated request; no
          sign-out is needed. Grants and revocations are retained for audit and
          are never deleted. This page administers the environment it is
          deployed in.
        </p>
      </div>
    </>
  );
}
